import api from '../config/api';

class FollowUpService {
  // Add follow-up to a lead
  static async addFollowUp(leadId, followUpData) {
    try {
      const response = await api.post(`/follow-up/add/${leadId}`, followUpData);
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error adding follow-up:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to add follow-up' 
      };
    }
  }

  // Get all follow-ups for a lead
  static async getLeadFollowUps(leadId) {
    try {
      const response = await api.get(`/follow-up/lead/${leadId}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching lead follow-ups:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to fetch follow-ups' 
      };
    }
  }

  // Update follow-up status
  static async updateFollowUp(leadId, followUpId, updateData) {
    try {
      const response = await api.put(`/follow-up/update/${leadId}/${followUpId}`, updateData);
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error updating follow-up:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to update follow-up' 
      };
    }
  }

  // Reschedule follow-up (convenience method)
  static async rescheduleFollowUp(leadId, followUpId, rescheduleData) {
    return this.updateFollowUp(leadId, followUpId, rescheduleData);
  }

  // Delete follow-up
  static async deleteFollowUp(leadId, followUpId) {
    try {
      const response = await api.delete(`/follow-up/delete/${leadId}/${followUpId}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error deleting follow-up:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to delete follow-up' 
      };
    }
  }

  // Get upcoming follow-ups (for dashboard)
  static async getUpcomingFollowUps(userId = null, days = 7) {
    try {
      const url = userId ? `/follow-up/upcoming/${userId}` : '/follow-up/upcoming';
      const response = await api.get(`${url}?days=${days}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching upcoming follow-ups:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to fetch upcoming follow-ups' 
      };
    }
  }

  // Get overdue follow-ups
  static async getOverdueFollowUps(userId = null) {
    try {
      const url = userId ? `/follow-up/overdue/${userId}` : '/follow-up/overdue';
      const response = await api.get(url);
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching overdue follow-ups:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to fetch overdue follow-ups' 
      };
    }
  }

  // Get follow-up statistics
  static async getFollowUpStats(userId = null) {
    try {
      const url = userId ? `/follow-up/stats/${userId}` : '/follow-up/stats';
      const response = await api.get(url);
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching follow-up stats:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to fetch follow-up statistics' 
      };
    }
  }

  // Helper method to format follow-up data for display
  static formatFollowUpForDisplay(followUp) {
    return {
      ...followUp,
      formattedDate: new Date(followUp.date).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      formattedNextDate: followUp.nextFollowUpDate ? 
        new Date(followUp.nextFollowUpDate).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }) : null,
      typeIcon: this.getFollowUpIcon(followUp.type),
      priorityColor: this.getPriorityColor(followUp.priority)
    };
  }

  // Get icon for follow-up type
  static getFollowUpIcon(type) {
    const icons = {
      call: '📞',
      email: '📧',
      meeting: '🤝',
      demo: '💻',
      proposal: '📋',
      message: '💬',
      assignment: '📌',
      other: '📝'
    };
    return icons[type] || '📝';
  }

  // Get color for priority
  static getPriorityColor(priority) {
    const colors = {
      low: 'text-green-600 bg-green-100',
      medium: 'text-yellow-600 bg-yellow-100',
      high: 'text-orange-600 bg-orange-100',
      urgent: 'text-red-600 bg-red-100'
    };
    return colors[priority] || colors.medium;
  }

  // Get follow-up type options for dropdowns
  static getFollowUpTypes() {
    return [
      { value: 'call', label: '📞 Phone Call', icon: '📞' },
      { value: 'email', label: '📧 Email', icon: '📧' },
      { value: 'meeting', label: '🤝 Meeting', icon: '🤝' },
      { value: 'demo', label: '💻 Demo', icon: '💻' },
      { value: 'proposal', label: '📋 Proposal', icon: '📋' },
      { value: 'message', label: '💬 Message', icon: '💬' },
      { value: 'other', label: '📝 Other', icon: '📝' }
    ];
  }

  // Get priority options for dropdowns
  static getPriorityOptions() {
    return [
      { value: 'low', label: 'Low Priority', color: 'text-green-600' },
      { value: 'medium', label: 'Medium Priority', color: 'text-yellow-600' },
      { value: 'high', label: 'High Priority', color: 'text-orange-600' },
      { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
    ];
  }
}

export default FollowUpService;
